/**
 * PDA seed byte arrays for the Morfi opinion program.
 * Each constant matches the corresponding Rust `b"..."` literal exactly.
 * Used as the first seed component in PDA derivation functions.
 */

/** Seed for Market PDA: b"market" */
export const MARKET_SEED: Uint8Array = new TextEncoder().encode('market');
/** Seed for MarketConfig PDA: b"config" (admin/init_market_config.rs). */
export const MARKET_CONFIG_SEED: Uint8Array = new TextEncoder().encode('config');
/** Seed for BackerFeeShareConfig PDA: b"backer_fee_share" (admin/init_backer_fee_share_config.rs). */
export const BACKER_FEE_SHARE_CONFIG_SEED: Uint8Array = new TextEncoder().encode(
  'backer_fee_share',
);
/** Seed for Outcome PDA: b"outcome" */
export const OUTCOME_SEED: Uint8Array = new TextEncoder().encode('outcome');
/** Seed for Outcome Mint PDA: b"outcome_mint" */
export const OUTCOME_MINT_SEED: Uint8Array = new TextEncoder().encode('outcome_mint');
/** Seed for Mint Authority PDA: b"mint_authority" */
export const MINT_AUTHORITY_SEED: Uint8Array = new TextEncoder().encode('mint_authority');
/** Seed for Market Group Mint PDA: b"market_group_mint" */
export const MARKET_GROUP_MINT_SEED: Uint8Array = new TextEncoder().encode('market_group_mint');
/** Seed for AMM Pool PDA: b"amm_pool" */
export const AMM_POOL_SEED: Uint8Array = new TextEncoder().encode('amm_pool');
/** Seed for Prize Pool PDA: b"prize_pool" */
export const PRIZE_POOL_SEED: Uint8Array = new TextEncoder().encode('prize_pool');
/** Seed for Claim Pool Vault PDA: b"claim_pool_vault" */
export const CLAIM_POOL_VAULT_SEED: Uint8Array = new TextEncoder().encode('claim_pool_vault');
/** Seed for Drip Pool Vault PDA: b"drip_pool_vault" */
export const DRIP_POOL_VAULT_SEED: Uint8Array = new TextEncoder().encode('drip_pool_vault');
/** Seed for Boost Vault PDA: b"boost_vault" */
export const BOOST_VAULT_SEED: Uint8Array = new TextEncoder().encode('boost_vault');
/** Seed for Challenge Vault PDA: b"challenge_vault" */
export const CHALLENGE_VAULT_SEED: Uint8Array = new TextEncoder().encode('challenge_vault');
/** Seed for Challenge Stake PDA: b"challenge_stake" */
export const CHALLENGE_STAKE_SEED: Uint8Array = new TextEncoder().encode('challenge_stake');
/** Seed for Roster Entry PDA: b"roster_entry" */
export const ROSTER_ENTRY_SEED: Uint8Array = new TextEncoder().encode('roster_entry');
/** Seed for Proposal PDA: b"proposal" */
export const PROPOSAL_SEED: Uint8Array = new TextEncoder().encode('proposal');
/** Seed for Proposal Stake PDA: b"proposal_stake" */
export const PROPOSAL_STAKE_SEED: Uint8Array = new TextEncoder().encode('proposal_stake');
/** Seed for Proposal Vault PDA: b"proposal_vault" */
export const PROPOSAL_VAULT_SEED: Uint8Array = new TextEncoder().encode('proposal_vault');
/** Seed for Boost Receipt PDA: b"boost_receipt" */
export const BOOST_RECEIPT_SEED: Uint8Array = new TextEncoder().encode('boost_receipt');
/** Seed for Claim Receipt PDA: b"claim_receipt" */
export const CLAIM_RECEIPT_SEED: Uint8Array = new TextEncoder().encode('claim_receipt');
/** Seed for Creator Profile PDA: b"creator_profile" */
export const CREATOR_PROFILE_SEED: Uint8Array = new TextEncoder().encode('creator_profile');
/** Seed for Referral Link PDA: b"referral_link" */
export const REFERRAL_LINK_SEED: Uint8Array = new TextEncoder().encode('referral_link');
/** Seed for Revenue Share Pool PDA (singleton): b"revenue_share_pool" */
export const REVENUE_SHARE_POOL_SEED: Uint8Array = new TextEncoder().encode('revenue_share_pool');
/** Seed for Revenue Share Recipient PDA: b"revenue_share" */
export const REVENUE_SHARE_SEED: Uint8Array = new TextEncoder().encode('revenue_share');
/** Seed for Handle Vault PDA: b"handle_vault" */
export const HANDLE_VAULT_SEED: Uint8Array = new TextEncoder().encode('handle_vault');
/** Seed for Gift Deposit PDA: b"gift_deposit" */
export const GIFT_DEPOSIT_SEED: Uint8Array = new TextEncoder().encode('gift_deposit');
/** Seed for GiftConfig PDA: b"gift_config" */
export const GIFT_CONFIG_SEED: Uint8Array = new TextEncoder().encode('gift_config');
/** Seed for GiftAdmin PDA (treasury authority): b"gift_admin" */
export const GIFT_ADMIN_SEED: Uint8Array = new TextEncoder().encode('gift_admin');
/** Seed for Gift PDA: b"gift" */
export const GIFT_SEED: Uint8Array = new TextEncoder().encode('gift');
/** Seed for Auth Nonce Tracker PDA: b"auth_nonce" */
export const AUTH_NONCE_SEED: Uint8Array = new TextEncoder().encode('auth_nonce');
/** Seed for Platform Config PDA (singleton): b"platform_config" */
export const PLATFORM_CONFIG_SEED: Uint8Array = new TextEncoder().encode('platform_config');
/** Seed for Thesis PDA: b"thesis" */
export const THESIS_SEED: Uint8Array = new TextEncoder().encode('thesis');

// v2 market-creation seeds — mirror contracts/markets/programs/markets/src
// /instructions/creation/{create_market_shell,init_outcome,finalize_market}.rs.
/** Generic "vault" namespace seed: b"vault" */
export const VAULT_SEED: Uint8Array = new TextEncoder().encode('vault');
/** BC quote vault sub-seed: b"bc_quote" */
export const BC_QUOTE_SEED: Uint8Array = new TextEncoder().encode('bc_quote');
/** Prize pool vault sub-seed: b"prize" */
export const PRIZE_SEED: Uint8Array = new TextEncoder().encode('prize');
/** Backer fee vault sub-seed: b"backer_fee" */
export const BACKER_FEE_SEED: Uint8Array = new TextEncoder().encode('backer_fee');
/** Bonding curve PDA seed: b"bc" (NOT "bonding_curve" — the Rust literal is just "bc") */
export const BC_SEED: Uint8Array = new TextEncoder().encode('bc');
/** Claim rights mint PDA seed: b"claim_rights_mint" */
export const CLAIM_RIGHTS_MINT_SEED: Uint8Array = new TextEncoder().encode('claim_rights_mint');
/** Backer share mint PDA seed: b"backer_share_mint" */
export const BACKER_SHARE_MINT_SEED: Uint8Array = new TextEncoder().encode('backer_share_mint');
/** Platform fee vault PDA seed: b"platform_fee_vault" */
export const PLATFORM_FEE_VAULT_SEED: Uint8Array = new TextEncoder().encode('platform_fee_vault');
/** MPL Token Metadata PDA seed: b"metadata" */
export const METADATA_SEED: Uint8Array = new TextEncoder().encode('metadata');
/** Anchor `#[event_cpi]` authority PDA seed: b"__event_authority" */
export const EVENT_AUTHORITY_SEED: Uint8Array = new TextEncoder().encode('__event_authority');
